// Customer-facing capabilities. Evidence and claim boundaries are documented in
// docs/audits/2026-09-19-landing-page/FEATURE-PROMISES.md.
export const LANDING_FEATURES = [
  {
    id: 'time',
    title: 'Live attendance & automated timesheets',
    summary: 'Real-time workforce presence without manual check-ins.',
    features: [
      ['Live team availability', 'See working, on-break, idle, and stopped states alongside recorded hours across your entire roster in real time.'],
      ['Automated timesheets', 'Accurate shift records of clock-in, clock-out, active time, breaks, and overtime without spreadsheet tracking.'],
      ['Manual time approvals', 'Empower employees to request manual time adjustments with structured manager review and audit reasons.'],
      ['Custom schedules & shifts', 'Configure expected shift hours, flexible clock-in windows, break policies, and idle exemptions.'],
    ],
    boundary: 'Desktop tracking requires a connected agent and permissions. Activity is operational context for review, not a standalone measure of work quality.',
  },
  {
    id: 'activity',
    title: 'Workday activity & automated timelapses',
    summary: '60-second daily shift recaps and visual proof of authentic work.',
    features: [
      ['60-second shift timelapses', 'Inspect an entire 8-hour workday in 60 seconds via automated MP4 timelapses rather than clicking hundreds of static images.'],
      ['Application & URL telemetry', 'Review productivity classifications and categorized tool usage with zero manual reporting.'],
      ['Privacy blurring & filters', 'Automatic privacy blur filters protect personal passwords and private browsing on workstation screens.'],
      ['Keystroke velocity telemetry', 'Measure active typing intensity and cadence without ever recording the characters typed.'],
    ],
    boundary: 'Screenshots can contain visible client data. Timelapses and input velocity require eligible plans. Review context before making personnel decisions.',
  },
  {
    id: 'protection',
    title: 'Tamper-proof OS watchdogs & continuity',
    summary: 'Self-healing background daemons that eliminate tracking gaps.',
    features: [
      ['5-second self-healing watchdog', 'Detached operating system service restarts the tracking daemon immediately if terminated in Task Manager.'],
      ['Shift exit guards', 'Restrict unauthorized closing or quitting during scheduled shifts with role-based administrative override.'],
      ['Interruption event audit', 'Log every agent termination attempt, reboot, and sleep state into an indisputable compliance trail.'],
      ['Offline screenshot buffering', 'Local encrypted cache queues captured screenshots during network outages and uploads automatically upon reconnect.'],
    ],
    boundary: 'Detection surfaces anomalies for review. Exit guards can be bypassed by power disconnects. Buffering is subject to local disk storage limits.',
  },
  {
    id: 'jiggler',
    title: 'Hardware mouse jiggler kinematics',
    summary: 'Mathematical trajectory physics expose synthetic USB dongles.',
    features: [
      ['Kinematic trajectory entropy', 'Analyzes cursor curvature and micro-velocity variations to differentiate human hands from motorized mechanical jigglers.'],
      ['Zero keylogging requirement', 'Detects hardware-level cheating through mathematical movement physics without invasive keystroke snooping.'],
      ['Real-time manager alerts', 'Surfaces synthetic movement anomalies directly in manager dashboards and instant Telegram alerts.'],
      ['Ghost hours elimination', 'Prevents unearned billable hours and false attendance across remote development teams.'],
    ],
    boundary: 'Kinematic analysis is probabilistic and intended to guide managerial review, not automatically apply disciplinary sanctions without context.',
  },
  {
    id: 'payroll',
    title: 'Indian statutory payroll & EPFO ECR',
    summary: 'Automate salary calculation, PF, ESIC, PT, and TDS in 1 click.',
    features: [
      ['Automated statutory deductions', 'Calculate exact employee and employer contributions for Provident Fund (PF), ESIC, State Professional Tax (PT), and TDS.'],
      ['Official EPFO ECR file export', 'Generate official formatted ECR text return files (.txt) ready for immediate upload to the EPFO unified employer portal.'],
      ['ESIC monthly return export', 'Export pre-validated ESIC return registers matching official government portal specifications.'],
      ['Instant salary slips & payouts', 'Generate compliant salary slips with itemized statutory breakdowns and execute payouts via 1-click Razorpay integration.'],
    ],
    boundary: 'Payroll automation requires an eligible paid plan and administrator access. Recording payouts does not transfer banking funds without banking gateway configuration.',
  },
  {
    id: 'remote',
    title: 'Zero-cost WebRTC remote desktop',
    summary: 'Built-in AnyDesk alternative with zero cloud relay bandwidth fees.',
    features: [
      ['1080p live screen streaming', 'View real-time workstation screens directly in your browser with ultra-low latency peer-to-peer WebRTC.'],
      ['Interactive remote desktop control', 'Provide instant remote technical assistance, debug developer environments, and control mouse/keyboard seamlessly.'],
      ['Two-way P2P file drawer', 'Transfer configuration files, diagnostics, and patches directly between supervisor and workstation without cloud uploads.'],
      ['Zero third-party license fees', 'Completely replaces expensive AnyDesk and TeamViewer subscriptions across your engineering organization.'],
    ],
    boundary: 'Peer-to-peer WebRTC streams directly between machines. Strict corporate firewall NATs may require an optional TURN relay configuration.',
  },
  {
    id: 'cloud',
    title: 'Private AWS cloud data sovereignty',
    summary: 'Store all visual records in your own AWS S3 bucket for under ₹6/month.',
    features: [
      ['Bring Your Own Cloud (BYOC)', 'Route captured screenshots and video timelapses directly into your company’s private AWS S3 bucket.'],
      ['Dedicated PostgreSQL database', 'Keep your database tables in your own AWS RDS PostgreSQL instance with zero vendor access to raw records.'],
      ['Empirical ₹6/seat/month cost', 'Production-verified storage cost of less than $0.07 (under ₹6 INR) per active employee per month on AWS S3 Standard.'],
      ['Client NDA & InfoSec compliance', 'Satisfy international enterprise clients requiring complete data isolation and zero third-party data custody.'],
    ],
    boundary: 'Dedicated AWS storage requires an active AWS account. Authentication and billing records remain securely managed on shared control planes.',
  },
  {
    id: 'security',
    title: 'Real-time executable download & USB audit',
    summary: 'Live endpoint security sentinels for remote and hybrid laptops.',
    features: [
      ['Executable ingress alerts', 'Detects `.exe`, `.dmg`, `.pkg`, `.sh`, and `.bat` file downloads the moment they begin on WFH machines.'],
      ['USB mass storage detection', 'Audits external USB thumb drives and portable disks with hardware device IDs to prevent confidential IP exfiltration.'],
      ['Smart distraction enforcement', 'Automatically terminates unauthorized gaming processes and closes non-work browser tabs during shift hours.'],
      ['Metadata-only inspection', 'Monitors file download extensions and security events with zero payload inspection, ensuring total privacy compliance.'],
    ],
    boundary: 'Ingress and USB detection requires desktop agent permissions on supported OS platforms. It does not replace full antivirus endpoint software.',
  },
  {
    id: 'dpdp',
    title: 'Privacy & DPDP Act 2023 compliance',
    summary: 'Verifiable work clarity without invasive or illegal spyware.',
    features: [
      ['Zero raw keylogging', 'Strictly avoids recording typed characters, protecting net-banking passwords and shielding firms from ₹250 Cr DPDP penalties.'],
      ['Explicit device operating tiers', 'User-controlled clock-out on Personal PCs; automated background governance on dedicated Company PCs.'],
      ['Employee transparency portal', 'Gives employees their own dashboard to review their hours, timelapses, leave balances, and salary slips.'],
      ['DLP pattern sentinels', 'Flags high-risk data loss events without retaining or inspecting confidential document contents.'],
    ],
    boundary: 'Company PC mode may remain active during breaks. Administrators must communicate company governance policies to employees prior to rollout.',
  },
  {
    id: 'integrations',
    title: 'Jira, GitHub & bi-directional project sync',
    summary: 'Connect tracked hours directly to client deliverables and tickets.',
    features: [
      ['Direct PM integration', 'Synchronize assigned tickets live from Jira Cloud, GitHub Issues, Asana, and ClickUp without paid connector tools.'],
      ['Automated ticket worklogs', 'Tracked time against tasks is automatically written back to client Jira and GitHub worklogs in real time.'],
      ['Client project budgeting', 'Track billable hours, project budgets, team allocations, and milestone delivery margins.'],
      ['Automated Telegram notifications', 'Receive instant alerts on your phone for shift absences, tamper attempts, and detected mouse jigglers.'],
    ],
    boundary: 'Third-party integrations require appropriate API credentials and permissions on the connected Jira, GitHub, or Asana workspaces.',
  },
];

export const LANDING_FAQS = [
  ['Who is Unified Workforce built for?', 'Unified Workforce is purpose-built for IT service companies, software engineering agencies, and remote knowledge teams. It automates workforce management—bringing attendance, real-time presence, projects, tamper-resistant proof of work, and Indian statutory payroll into one unified cockpit.'],
  ['How does Unified Workforce automate company management without feeling like invasive surveillance?', 'We reject invasive spyware like raw keyloggers that snoop on passwords and destroy team trust. Instead, we provide clear live presence indicators (working, break, stopped), 60-second shift timelapses, and physics-based movement verification. This gives leadership complete operational clarity while giving employees a dignified, transparent workspace.'],
  ['How does Indian Statutory Payroll work in Unified Workforce?', 'Unified Workforce automatically converts verified shift attendance into compliant payroll calculations. It computes employee and employer Provident Fund (PF/EPS), ESIC, State Professional Tax (PT), and TDS deductions. In one click, you can generate the official EPFO ECR file ready for direct upload to the EPFO portal, download salary slips, and execute payouts via Razorpay.'],
  ['How does Hardware Jiggler Kinematics Detection work?', 'Unlike basic software checkers that only monitor idle timers, our physics engine analyzes cursor velocity curvature, linear trajectory entropy, and micro-interval timing. This identifies both software bots and physical mechanical USB mouse jigglers used to simulate fake desk presence.'],
  ['How does Zero-Cost WebRTC Remote Desktop work?', 'Unified Workforce establishes direct peer-to-peer (P2P) WebRTC connections between the supervisor’s browser and the employee’s desktop. Live 1080p screen viewing, interactive remote mouse control, and file transfers stream directly between machines with zero intermediary cloud media relay bandwidth bills.'],
  ['Can we store screenshots in our own company AWS S3 bucket?', 'Yes. Unified Workforce supports Bring Your Own Cloud (BYOC) for AWS S3. You can connect your private AWS bucket in organization settings so all captured employee screenshots and timelapses stream directly into your cloud. Your company retains full data custody and encryption control, helping satisfy strict client NDAs and enterprise compliance audits.'],
  ['How much does storing screenshots on our own AWS server actually cost?', 'AWS S3 storage is surprisingly affordable. Measured empirical data from an active full-time employee (taking screenshots every ~2 minutes across ~138 monthly hours) generates about 1.08 GB of media per month. On AWS S3 Standard, this costs less than $0.07 (under ₹6 INR) per user per month, with bandwidth covered under AWS’s 100 GB monthly free egress tier.'],
  ['Why is Unified Workforce safer than keyloggers under India’s DPDP Act, 2023?', 'Raw keyloggers capture sensitive passwords, OTPs, and personal net-banking credentials. Under India’s Digital Personal Data Protection Act (DPDP) 2023, data leaks involving sensitive personal data carry penalties up to ₹250 Crore. Unified Workforce uses keystroke velocity telemetry and pattern detection to verify active work without storing any typed text.'],
  ['Does tracking stop outside work sessions?', 'Personal-PC tracking follows work-session and break controls. Company-PC policies can keep background tracking active outside manually started sessions, including during breaks. Administrators should explain the selected policy and captured data before installing the desktop app.'],
  ['What is included in the free trial?', 'Start immediately with our free tier or 14-day full feature trial. Test live team presence, automated timesheets, up to two teams, and 10 GB of cloud storage with zero credit card required.'],
  ['Does Unified Workforce integrate with Jira, Asana, ClickUp, and GitHub?', 'Yes. Unified Workforce provides direct, bi-directional project management synchronization with Jira Cloud, Asana, ClickUp, and GitHub Issues. Assigned tickets automatically sync to employee project boards, and tracked work time is automatically written back to ticket worklogs in real time without third-party aggregator surcharges.'],
  ['How do real-time security alerts work with Telegram?', 'Administrators can connect their free Telegram bot in seconds. Real-time alerts for force-killed tracking daemons, unauthorized sign-outs, late clock-ins, shift absence, and detected mouse jigglers are dispatched instantly to your phone or management group chat.'],
];

export const BATTLECARD_PILLARS = [
  {
    id: 'os-watchdog',
    title: 'Tamper-Proof OS Watchdogs',
    subtitle: 'Auto-Start on Kill & Exit Tamper Guard',
    tag: 'Anti-Tamper Architecture',
    description: 'System service daemon auto-restarts tracker within 5 seconds if killed in Task Manager; policy-enforced exit guards stop unauthorized sign-out during shifts.',
  },
  {
    id: 'remote-assistance',
    title: 'Zero-Cost WebRTC Remote Assistance',
    subtitle: 'P2P 1080p Live Screen & Remote Desktop',
    tag: 'P2P Zero Cloud Bandwidth',
    description: 'Peer-to-peer 1080p live stream, AnyDesk-style remote control, and direct two-way P2P file transfer with zero cloud server bandwidth costs.',
  },
  {
    id: 'cloud-sovereignty',
    title: 'Private Cloud Sovereignty',
    subtitle: 'Dedicated AWS S3 & DB Data Custody',
    tag: 'Bring Your Own Cloud (BYOC)',
    description: 'Route all employee screenshots, timelapses, and data into your company’s private AWS account for under ₹6 per employee/month.',
  },
  {
    id: 'jiggler-proof',
    title: 'Hardware-Level Jiggler Proof',
    subtitle: 'Kinematics & Micro-Movement Physics',
    tag: 'Anti-Spoofing Physics Engine',
    description: 'Kinematic trajectory and curvature entropy algorithms expose physical USB mouse jigglers and synthetic input scripts without keylogging.',
  },
];

export const BATTLECARD_ROWS = [
  {
    id: 'os-watchdog',
    name: 'Tamper-Proof OS Watchdog (Self-Heals in 5s)',
    iconKey: 'cpu',
    competitors: {
      unifiedWorkforce: { status: 'yes', label: 'Native 5s auto-restart service & exit tamper guard' },
      superworks: { status: 'no', label: 'No watchdog; easily closed from Task Manager' },
      timeChamp: { status: 'warning', label: 'Partial daemon; often killed or flagged by AV' },
      workfolio: { status: 'no', label: 'Standard desktop app; dies on process kill' },
      hubstaff: { status: 'no', label: 'No self-healing; silent tracking termination' },
    },
  },
  {
    id: 'hardware-jiggler',
    name: 'Hardware Mouse Jiggler Kinematics Detection',
    iconKey: 'mouse',
    competitors: {
      unifiedWorkforce: { status: 'yes', label: 'Mathematical trajectory & velocity curvature physics' },
      superworks: { status: 'no', label: 'Only basic idle timer; easily fooled' },
      timeChamp: { status: 'warning', label: 'Software checks only; misses physical USB devices' },
      workfolio: { status: 'no', label: 'No jiggler detection capabilities' },
      hubstaff: { status: 'partial', label: 'Basic process scan; bypassed by Amazon USB jigglers' },
    },
  },
  {
    id: 'remote-desktop',
    name: 'Built-in AnyDesk Remote Desktop & P2P Control',
    iconKey: 'cast',
    competitors: {
      unifiedWorkforce: { status: 'yes', label: 'Full remote takeover & 2-way file drawer at ₹0 bandwidth' },
      superworks: { status: 'no', label: 'No remote assistance; requires separate licenses' },
      timeChamp: { status: 'no', label: 'View-only video; cannot take over or send files' },
      workfolio: { status: 'no', label: 'No remote desktop capabilities' },
      hubstaff: { status: 'no', label: 'No remote desktop capabilities' },
    },
  },
  {
    id: 'p2p-live-screen',
    name: '1080p WebRTC Live Screen Streaming',
    iconKey: 'video',
    competitors: {
      unifiedWorkforce: { status: 'yes', label: 'Instant peer-to-peer browser stream with zero cloud lag' },
      superworks: { status: 'no', label: 'Static screenshot grids only' },
      timeChamp: { status: 'warning', label: 'Heavy cloud relay that congests office internet' },
      workfolio: { status: 'no', label: 'Static screenshots and video timelapses only' },
      hubstaff: { status: 'no', label: 'Static periodic screenshots only' },
    },
  },
  {
    id: 'statutory-payroll',
    name: 'Indian Statutory Payroll (PF, ESIC, PT, TDS & EPFO ECR)',
    iconKey: 'wallet',
    competitors: {
      unifiedWorkforce: { status: 'yes', label: 'Full statutory deductions + official EPFO ECR export' },
      superworks: { status: 'yes', label: 'Full Indian statutory payroll features' },
      timeChamp: { status: 'no', label: 'Basic hours export only; no statutory tax filing' },
      workfolio: { status: 'no', label: 'No payroll or statutory tax capabilities' },
      hubstaff: { status: 'no', label: 'US-centric contractor payouts only (PayPal/Wise)' },
    },
  },
  {
    id: 'aws-sovereignty',
    name: 'Private AWS S3 & RDS Data Custody (< ₹6/mo)',
    iconKey: 'database',
    competitors: {
      unifiedWorkforce: { status: 'yes', label: 'Direct BYOC into your AWS account; zero vendor access' },
      superworks: { status: 'no', label: 'Shared multi-tenant database' },
      timeChamp: { status: 'no', label: 'Media stored on vendor servers' },
      workfolio: { status: 'no', label: 'Screenshots stored on shared cloud' },
      hubstaff: { status: 'no', label: 'Closed proprietary AWS cluster' },
    },
  },
  {
    id: 'dpdp-compliance',
    name: 'DPDP Act 2023 Safe (Zero Raw Keylogging)',
    iconKey: 'shield',
    competitors: {
      unifiedWorkforce: { status: 'yes', label: 'Activity velocity & DLP without recording passwords' },
      superworks: { status: 'yes', label: 'No keylogger installed' },
      timeChamp: { status: 'warning', label: 'Raw keylogger risks ₹250 Cr DPDP penalty on breach' },
      workfolio: { status: 'yes', label: 'No keylogger installed' },
      hubstaff: { status: 'yes', label: 'No raw keylogger installed' },
    },
  },
  {
    id: 'video-timelapses',
    name: 'Automated 60-Second Daily Work Timelapses',
    iconKey: 'video',
    competitors: {
      unifiedWorkforce: { status: 'yes', label: 'Native MP4 timelapses with automated privacy blur' },
      superworks: { status: 'no', label: 'Static screenshot grids only' },
      timeChamp: { status: 'no', label: 'Raw continuous video only' },
      workfolio: { status: 'yes', label: 'Good video timelapses' },
      hubstaff: { status: 'no', label: 'Static screenshots only' },
    },
  },
  {
    id: 'seat-pricing',
    name: 'Flat Transparent Per-Seat Pricing',
    iconKey: 'calculator',
    competitors: {
      unifiedWorkforce: { status: 'yes', label: '₹228/mo annual (₹285/mo monthly) flat with all modules & statutory payroll' },
      superworks: { status: 'warning', label: '₹149/mo base + add-ons for full compliance' },
      timeChamp: { status: 'warning', label: '₹350–₹750/mo with bandwidth tiers' },
      workfolio: { status: 'warning', label: '₹150/mo for basic screenshot tracking' },
      hubstaff: { status: 'no', label: '$7–$10 USD (~₹580–₹830/mo) per seat' },
    },
  },
];
